import {
  resolveCategoryColor,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import { isExperienceEvent } from '@/features/events/lib/event.lib';
import type {
  DecoratedEvent,
  DecoratedExperienceEvent,
} from '@/features/events/types';
import type { PageExperience } from '@/features/experiences/types';
import {
  type ExperienceCategory,
  type ExperienceColorMode,
  FeedOriginKind,
  type ExperienceFeed,
} from '@/types/gql.generated';

// Whether a source is still delivering events. An experience-backed source whose
// access was revoked keeps its row and every setting on it, so it stays in the
// Sources panel to explain itself, but it has no events and does not belong in
// any list of things to filter by.
export const isFeedAccessible = (
  feed: Pick<ExperienceFeed, 'accessRevokedAt'>
): boolean => !feed.accessRevokedAt;

// Whether the origin experience behind a source has been deleted. This is the
// one revocation that cannot be undone: there is no one left to grant access,
// so the panel explains it instead of offering "Request access". Deletion is
// the reason the source stopped only while access is actually revoked; a live
// source on a deleted origin has not gone dark yet.
export const isFeedOriginDeleted = (
  feed: Pick<ExperienceFeed, 'accessRevokedAt' | 'originDeletedAt'>
): boolean => !isFeedAccessible(feed) && Boolean(feed.originDeletedAt);

// The id of the feed/source an event originated from. External (Google/ICS) origins
// carry it on origin.sourceId; experience-backed feed origins carry it on
// origin.experienceId. Both key to the ExperienceFeed id.
const getOriginFeedId = (event: DecoratedExperienceEvent): string | null =>
  event.origin?.sourceId ?? event.origin?.experienceId ?? null;

export const getEventFeed = (
  experience: PageExperience,
  event: DecoratedExperienceEvent
): ExperienceFeed | null => {
  const feedId = getOriginFeedId(event);

  if (!feedId) {
    return null;
  }

  return experience.feeds.find((feed) => feed.id === feedId) ?? null;
};

// Match an event to a specific feed by id — origin.sourceId for external sources,
// origin.experienceId for internal feeds, both keyed to the feed's id. Id-based
// matching disambiguates sources that share a title.
export const eventMatchesFeed = (
  event: DecoratedEvent,
  feed: ExperienceFeed
): boolean => isExperienceEvent(event) && getOriginFeedId(event) === feed.id;

// Color for an event sourced from a feed or external calendar. EventOrigin.color
// is populated for both kinds, so this resolves the right color even for external
// sources, which have no entry in experience.feeds.
export const getEventOriginColor = (
  event: DecoratedExperienceEvent
): string | null => event.origin?.color ?? null;

// Resolved indicator color for an event sourced from a feed. Prefers the feed's
// assigned category resolved through the experience palette and bold/light
// colorMode, falling back to the feed's flat color, then the external-source
// origin color, else null for events that belong directly to the experience.
// Pass an explicit colorMode to override the experience's — feed indicator dots
// force the bold variant for contrast at their small render size.
export const getEventSourceColor = (
  experience: PageExperience,
  event: DecoratedEvent,
  colorMode: ExperienceColorMode | null | undefined = experience.colorMode
): string | null => {
  if (!isExperienceEvent(event)) {
    return null;
  }
  const feed = getEventFeed(experience, event);
  return (
    (feed?.category
      ? resolveCategoryColor(
          toPaletteColorName(feed.category.colorName),
          experience.colorPalette,
          colorMode
        )
      : null) ??
    feed?.color ??
    getEventOriginColor(event)
  );
};

// The category an event should *display* as. Events pulled in from a source
// normally carry no category of their own — the assignment lives on the source
// (Sources panel → Source defaults) and its events inherit it, so an imported
// calendar is colored by default instead of rendering uncategorized. A per-event
// category still wins, which is what lets one event from a source be recategorized
// without detaching it from the source.
//
// Display only. Assignment surfaces (metadata chips, the category picker, the edit
// modal) must keep reading event.metadata.category directly: showing an inherited
// category there would read as an assignment the picker beside it then contradicts,
// and an edit would write the inherited value onto the event as its own.
export const getDisplayCategory = (
  experience: PageExperience,
  event: DecoratedEvent
): ExperienceCategory | null => {
  if (!isExperienceEvent(event)) {
    return null;
  }

  return event.metadata.category ?? getInheritedCategory(experience, event);
};

// The half of getDisplayCategory an event does not own: the category its source
// lends it, or null.
export const getInheritedCategory = (
  experience: PageExperience,
  event: DecoratedEvent
): ExperienceCategory | null =>
  isExperienceEvent(event)
    ? (getEventFeed(experience, event)?.category ?? null)
    : null;

// Stable per-source key for grouping or deduping event indicators: known feeds keyed
// by id, external sources with no feed entry keyed by their origin color. Returns null
// for events that belong directly to the experience.
export const getEventSourceKey = (
  experience: PageExperience,
  event: DecoratedEvent
): string | null =>
  isExperienceEvent(event)
    ? (getEventFeed(experience, event)?.id ?? getEventOriginColor(event))
    : null;

// True when an event originates from a feed or external calendar source rather than
// belonging directly to the experience collection. Resolves to a known feed, or — for
// external (Google/ICS) sources not present in experience.feeds — is matched by origin kind.
export const isFeedEvent = (
  experience: PageExperience,
  event: DecoratedEvent
): boolean =>
  isExperienceEvent(event) &&
  (getEventFeed(experience, event) !== null ||
    event.origin?.kind === FeedOriginKind.Google ||
    event.origin?.kind === FeedOriginKind.Ics);
